"use strict";

Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.hasConfirmedSingleSupplementForPax = exports.hasMyOwnRoom = exports.hasPermission = exports.getCountryCodeFromCustomerData = exports.getTravelAcknowledgementsWithLinks = exports.getLink = undefined;

var _travelReady = require("./travel-ready");

var _FormLink = require("../../components/forms/FormLink");

var _FormLink2 = _interopRequireDefault(_FormLink);

var _links = require("./links");

var _lodash = require("lodash");

var _lodash2 = _interopRequireDefault(_lodash);

var _react = require("react");

var _react2 = _interopRequireDefault(_react);

var _permissions = require("./permissions");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/**
 * Returns a link from a nested country-to-company map, with multiple fallback strategies.
 *
 * Lookup order:
 * 1. Exact match: map[countryCode][bookingCompanyId]
 * 2. Fallback company in fallback country: map[GLOBAL][bookingCompanyId]
 * 3. Fallback company in fallback country (ID 1): map[GLOBAL][1]
 *
 * Returns null if no match is found.
 *
 * @param {number} bookingCompanyId - ID of the booking company.
 * @param {Object} map - Country-to-company-to-link mapping object.
 * @param {string} countryCode - Country code to lookup in the map.
 * @returns {string|null} The link if found, otherwise null.
 */
var getLink = exports.getLink = function getLink(bookingCompanyId, map, countryCode) {
    var fallbackCountryCode = "GLOBAL";
    var fallbackBookingCompanyId = 1;

    var activeCountryMap = map[countryCode] || map[fallbackCountryCode];

    return activeCountryMap && activeCountryMap[bookingCompanyId] || map[fallbackCountryCode] && map[fallbackCountryCode][bookingCompanyId] || map[fallbackCountryCode] && map[fallbackCountryCode][fallbackBookingCompanyId] || null;
};

/**
 * Wraps a keyword in an acknowledgement string with a clickable FormLink component.
 *
 * - Searches for a language-specific keyword in the text.
 * - If found, wraps it in a link.
 * - If no keyword is found, renders the full text followed by the link separately.
 *
 * @param {string} acknowledgement - The raw acknowledgement string.
 * @param {string} language - Language code (e.g., 'en', 'de').
 * @param {string|null} link - URL to embed in the acknowledgement (optional).
 * @returns {JSX.Element} The final React fragment with or without a link.
 */
var wrapTextWithFormLink = function wrapTextWithFormLink(acknowledgement, language, link) {
    var keyword = _travelReady.TRAVEL_READY_ACKNOWLEDGEMENT_KEYWORDS[language].find(function (value) {
        return acknowledgement.includes(value);
    });

    // Return link after full text if no keyword found.
    if (!keyword) {
        return _react2.default.createElement(
            "div",
            null,
            _react2.default.createElement(
                "div",
                null,
                acknowledgement
            ),
            link && _react2.default.createElement(
                _FormLink2.default,
                { link: link },
                link
            )
        );
    }

    var index = acknowledgement.indexOf(keyword);
    var before = acknowledgement.slice(0, index);
    var after = acknowledgement.slice(index + keyword.length);

    return _react2.default.createElement(
        "div",
        null,
        before,
        link ? _react2.default.createElement(
            _FormLink2.default,
            { link: link },
            keyword
        ) : keyword,
        after
    );
};

/**
 * Generates a list of acknowledgement strings with embedded links for travel readiness.
 *
 * - Retrieves the text list from a language-specific map.
 * - Finds links using the company ID and country code.
 * - Inserts the link into each acknowledgement using the relevant keyword.
 *
 * @param {number} bookingCompanyId - ID of the booking company.
 * @param {string} language - Language code (e.g., 'en', 'de').
 * @param {string} countryCode - Country code for link selection (e.g., 'ZA', 'GLOBAL').
 * @returns {JSX.Element[]} Array of formatted React elements with embedded links.
 */
var getTravelAcknowledgementsWithLinks = exports.getTravelAcknowledgementsWithLinks = function getTravelAcknowledgementsWithLinks(bookingCompanyId, language, countryCode) {
    var langMap = _travelReady.TRAVEL_READY_ACKNOWLEDGEMENT_MAP[language] || _travelReady.TRAVEL_READY_ACKNOWLEDGEMENT_MAP.en; // fallback to English if missing.

    var texts = langMap[bookingCompanyId] || langMap[1]; // fallback to default company ID.

    var links = getLink(bookingCompanyId, _links.TRAVEL_ACKNOWLEDGEMENT_LINK_MAP, countryCode);

    return texts.map(function (text, i) {
        return wrapTextWithFormLink(text, language, links[i] || null);
    });
};

/**
 * Returns the customer's country code from booking metadata.
 *
 * If the country code is missing or invalid, it returns null — allowing downstream
 * logic (like getLink) to handle fallback behavior.
 *
 * @param {Object} customer - Customer object containing address information.
 * @returns {string|null} Returns country code if found, otherwise null.
 */
var getCountryCodeFromCustomerData = exports.getCountryCodeFromCustomerData = function getCountryCodeFromCustomerData(customer) {
    if (!customer) return null;

    return _lodash2.default.get(customer, "address.country.id", null);
};

/**
 * Determines whether any of the roles listed in the `editable_by` field
 * of a requirement are included in the `PERMISSIONS_LIST`.
 *
 * @param {{ editable_by?: string[] }} requirement - The requirement object.
 * @returns {boolean} `true` if at least one role in `editable_by` is allowed, otherwise `false`.
 */
var hasPermission = exports.hasPermission = function hasPermission(requirement) {
    var editableBy = _lodash2.default.get(requirement, "editable_by", []);

    return editableBy.some(function (role) {
        return _permissions.PERMISSIONS_LIST.includes(role);
    });
};

/**
 * Checks if the rooming information requirement has "MY_OWN_ROOM".
 *
 * @param {{ Object }} requirementSet The full requirement set JSON.
 * @returns {boolean} Return true if the rooming detail resource_fields contain reason === "MY_OWN_ROOM"
 */
var hasMyOwnRoom = exports.hasMyOwnRoom = function hasMyOwnRoom(requirementSet) {
    var roomingDetails = _lodash2.default.get(requirementSet, "reqSetsByCode.ROOMING_INFORMATION.reqsByCode.ROOMING_DETAILS",
    // fallback if the data sometimes comes flattened without ROOMING_INFORMATION
    _lodash2.default.get(requirementSet, "reqsByCode.ROOMING_DETAILS", []));

    return _lodash2.default.some(roomingDetails, function (req) {
        return _lodash2.default.some(req && req.resource_fields, { field: "reason", value: "MY_OWN_ROOM" });
    });
};

/**
 * Checks whether a pax has a confirmed single supplement service.
 *
 * Rules:
 * 1. Service type must be "single_supplement_services"
 * 2. Service status must be "Confirmed"
 * 3. Pax must exist in service.customers
 *
 * @param {Object[]} bookingServices - Array of booking service resources.
 * @param {string|number|Object} customerId - Customer id or object containing `id`.
 * @returns {boolean} True if pax has confirmed single supplement, otherwise false.
 */
var hasConfirmedSingleSupplementForPax = exports.hasConfirmedSingleSupplementForPax = function hasConfirmedSingleSupplementForPax(bookingServices, customerId) {
    if (!Array.isArray(bookingServices)) {
        return false;
    }

    var resolvedCustomerId = _lodash2.default.get(customerId, "id", customerId);
    if (!resolvedCustomerId) {
        return false;
    }

    var customerIdStr = String(resolvedCustomerId);

    return bookingServices.some(function (service) {
        if (!service) {
            return false;
        }

        var isSingleSupplementService = service.type === "single_supplement_services";
        var isConfirmed = service.status === "Confirmed";
        var customers = Array.isArray(service.customers) ? service.customers : [];
        var paxIsInServiceCustomers = customers.some(function (customer) {
            return String(_lodash2.default.get(customer, "id", "")) === customerIdStr;
        });

        return isSingleSupplementService && isConfirmed && paxIsInServiceCustomers;
    });
};


//////////////////
// WEBPACK FOOTER
// ./app/utils/mappings/utils.js
// module id = 88
// module chunks = 0